import { DtoDuesLineItem } from '@/common/types/membership/DtoLineItem';

export type DuesPreviewDiscount = [total: number, type: string, taxable: boolean];

/**
 * DuesLineItemService collapses leftover dues to Free Membership when their
 * sum is negative, so leftover credits must not offset add-on checkout totals.
 */
export function getBillableDuesLineItems<T>(items: T[], getPrice: (item: T) => number): T[] {
    if (!items.length) {
        return items;
    }

    const sum = items.reduce((acc, item) => acc + (getPrice(item) || 0), 0);
    return sum < 0 ? [] : items;
}

export function expandPerEligibleItemPrices(duesRule: DtoDuesLineItem): number[] {
    return duesRule.perEligibleItemPrices && duesRule.perEligibleItemPrices.length > 0
        ? duesRule.perEligibleItemPrices
        : [duesRule.line.price ?? 0];
}

export function getPerEligibleItemDuesDiscounts(duesRules: DtoDuesLineItem[], discountAmount: number): DuesPreviewDiscount[] {
    const discounts: DuesPreviewDiscount[] = [];

    duesRules.forEach(duesRule => {
        expandPerEligibleItemPrices(duesRule).forEach(unitPrice => {
            if (unitPrice > 0) {
                discounts.push([Math.min(unitPrice, discountAmount), "dues", !!duesRule.line.taxable]);
            }
        });
    });

    return discounts;
}

/** Percentage Per Eligible Item bases: one entry per positive seat/dues unit (not the grouped line). */
export function getPerEligibleItemPercentageBases(duesRules: DtoDuesLineItem[]): DuesPreviewDiscount[] {
    const bases: DuesPreviewDiscount[] = [];

    duesRules.forEach(duesRule => {
        const taxable = !!duesRule.line.taxable;
        expandPerEligibleItemPrices(duesRule).forEach(unitPrice => {
            if (unitPrice > 0) {
                bases.push([unitPrice, "dues", taxable]);
            }
        });
    });

    return bases;
}

export function limitDiscountsByAvailableUses(discounts: DuesPreviewDiscount[], availableUses?: number | null): DuesPreviewDiscount[] {
    if (availableUses == null || availableUses >= discounts.length) {
        return discounts;
    }

    return discounts
        .filter(x => x[0] > 0)
        .sort((a, b) => b[0] - a[0])
        .slice(0, availableUses);
}

/** Match PromoCodeService MidpointRounding.AwayFromZero to 2 dp for positive money. */
export function roundMoney(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Percentage Per Eligible Item dues: round each seat unit base × percent, then sum
 * (same as GeneratePromoCodeLineItems unit discounts). Add-on PEI preview should
 * still round once on the summed bases to match product charge grouping.
 */
export function sumRoundedPercentageOfBases(bases: ReadonlyArray<number | DuesPreviewDiscount>, percent: number): number {
    let total = 0;
    for (const base of bases) {
        const amount = typeof base === 'number' ? base : base[0];
        total += roundMoney(amount * percent);
    }
    return total;
}
